// Narrowing helpers + envelope type predicates. Every builder reads the wire
// payload through these instead of hand-rolling `typeof x === 'string'` chains.

import type {
  StructuredClarify,
  StructuredClarifyForm,
  StructuredContactsCreated,
  StructuredDashboard,
  StructuredDataTable,
  StructuredDownload,
  StructuredEmailCompose,
  StructuredIcp,
  StructuredResume,
  StructuredTable,
} from './types';

export const str = (v: unknown): string | undefined =>
  typeof v === 'string' ? v : undefined;

export const num = (v: unknown): number | undefined =>
  typeof v === 'number' ? v : undefined;

/** Positive, finite number — Go zero-values arrive as 0 and mean "absent". */
export const posNum = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : undefined;

export const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

export const strArr = (v: unknown): string[] =>
  arr(v).filter((s): s is string => typeof s === 'string');

/** Ids travel as either a number or its string form. */
export const toNum = (v: unknown): number =>
  typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;

export const toId = (v: unknown): string | undefined =>
  typeof v === 'number' ? String(v) : typeof v === 'string' ? v : undefined;

const isType = (data: unknown, type: string): boolean =>
  typeof data === 'object' &&
  data !== null &&
  'type' in data &&
  (data as { type?: unknown }).type === type;

export const isStructuredTable = (data: unknown): data is StructuredTable =>
  typeof data === 'object' && data !== null && 'type' in data;

export const isStructuredDashboard = (d: unknown): d is StructuredDashboard =>
  isType(d, 'dashboard') &&
  typeof (d as StructuredDashboard).title === 'string';

export const isStructuredDownload = (d: unknown): d is StructuredDownload =>
  isType(d, 'download') &&
  typeof (d as StructuredDownload).name === 'string' &&
  typeof (d as StructuredDownload).url === 'string';

export const isStructuredClarify = (data: unknown): data is StructuredClarify =>
  isType(data, 'clarify');

export const isStructuredClarifyForm = (
  data: unknown
): data is StructuredClarifyForm => isType(data, 'clarify_form');

export const isStructuredEmailCompose = (
  data: unknown
): data is StructuredEmailCompose => isType(data, 'email_compose');

export const isStructuredIcp = (data: unknown): data is StructuredIcp =>
  isType(data, 'icp');

export const isStructuredResume = (data: unknown): data is StructuredResume =>
  isType(data, 'resume');

export const isStructuredDataTable = (
  data: unknown
): data is StructuredDataTable => isType(data, 'data_table');

export const isContactsCreated = (
  data: unknown
): data is StructuredContactsCreated =>
  typeof data === 'object' &&
  data !== null &&
  'kind' in data &&
  (data as { kind?: unknown }).kind === 'contacts_created';
